import {
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconBell,
  IconBuilding,
  IconChevronRight,
  IconListCheck,
  IconPlug,
  IconRefresh,
  IconUsers,
} from '@tabler/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailRow, DisabledBadge } from '../../components';
import { useAccount } from '../../queries/use-accounts';
import { IntegrationInstallationView } from './common/integration-installation-view';
import { IntegrationsTab } from './common/integrations-tab';
import { JobView } from './common/job-view';
import { JobsTab } from './common/jobs-tab';
import { NotificationSettingsTab } from './common/notification-settings-tab';
import { UsersTab } from './common/users-tab';

const ACCOUNT_TABS = ['users', 'integrations', 'jobs', 'notifications'] as const;
type AccountTab = (typeof ACCOUNT_TABS)[number];

function resolveTab(value: string | undefined): AccountTab {
  return (ACCOUNT_TABS as readonly string[]).includes(value ?? '')
    ? (value as AccountTab)
    : 'users';
}

function formatDate(value: string | undefined): string {
  return value ? new Date(value).toLocaleString() : '—';
}

export function AccountContainer() {
  const { accountId, tab, installationId, detailTab, jobId } = useParams<{
    accountId: string;
    tab?: string;
    installationId?: string;
    detailTab?: string;
    jobId?: string;
  }>();
  const navigate = useNavigate();
  const activeTab = resolveTab(tab);
  const {
    data: account,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useAccount(accountId ?? null);

  return jobId && accountId ? (
    <JobView accountId={accountId} jobId={jobId} />
  ) : installationId && accountId ? (
    <IntegrationInstallationView
      accountId={accountId}
      installationId={installationId}
      detailTab={detailTab}
    />
  ) : (
    <Stack spacing="xl">
      <Breadcrumbs separator={<IconChevronRight size={12} />}>
        <Anchor component={Link} to="/accounts" color="dimmed" size="sm">
          Accounts
        </Anchor>
        <Text size="sm">{account?.name ?? accountId}</Text>
      </Breadcrumbs>

      <Box>
        <Group position="apart" align="flex-start" noWrap>
          <Group spacing="md" align="center" noWrap>
            <ThemeIcon size={42} radius="md" variant="light" color="bagel">
              <IconBuilding size={22} />
            </ThemeIcon>
            <Box>
              <Group spacing="sm" align="center">
                <Title order={2}>{account?.name ?? 'Account'}</Title>
                {account && <DisabledBadge disabled={account.disabled} />}
              </Group>
              <Text size="xs" color="dimmed" sx={{ fontFamily: 'monospace' }}>
                {accountId}
              </Text>
            </Box>
          </Group>
          <Button
            leftIcon={<IconRefresh size={16} />}
            variant="subtle"
            color="bagel"
            size="sm"
            onClick={() => refetch()}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Group>
      </Box>

      {account ? (
        <>
          <Paper withBorder p="lg">
            <Group spacing="xl" align="flex-start" noWrap={false}>
              <DetailRow label="Name" value={account.name ?? '—'} />
              <DetailRow label="Domain" value={account.domain ?? '—'} />
              <DetailRow label="Currency" value={account.currency ?? '—'} />
              <DetailRow label="Timezone" value={account.timezone ?? '—'} />
              <DetailRow label="Disabled" value={<DisabledBadge disabled={account.disabled} />} />
              <DetailRow label="Created" value={formatDate(account.createdAt)} />
              <DetailRow
                label="ID"
                value={
                  <Text size="sm" sx={{ fontFamily: 'monospace' }}>
                    {account._id}
                  </Text>
                }
              />
            </Group>
          </Paper>

          <Tabs
            value={activeTab}
            onTabChange={(value) => {
              if (!value || !accountId) return;
              navigate(`/account/${accountId}/${value}`);
            }}
            color="bagel"
          >
            <Tabs.List>
              <Tabs.Tab value="users" icon={<IconUsers size={16} />}>
                Users
              </Tabs.Tab>
              <Tabs.Tab value="integrations" icon={<IconPlug size={16} />}>
                Integrations
              </Tabs.Tab>
              <Tabs.Tab value="jobs" icon={<IconListCheck size={16} />}>
                Jobs
              </Tabs.Tab>
              <Tabs.Tab value="notifications" icon={<IconBell size={16} />}>
                Notifications
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="users" pt="md">
              {accountId ? (
                <UsersTab accountId={accountId} />
              ) : (
                <Text color="dimmed">Missing account id.</Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="integrations" pt="md">
              {accountId ? (
                <IntegrationsTab accountId={accountId} />
              ) : (
                <Text color="dimmed">Missing account id.</Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="jobs" pt="md">
              {accountId ? (
                <JobsTab accountId={accountId} />
              ) : (
                <Text color="dimmed">Missing account id.</Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="notifications" pt="md">
              {accountId ? (
                <NotificationSettingsTab accountId={accountId} />
              ) : (
                <Text color="dimmed">Missing account id.</Text>
              )}
            </Tabs.Panel>
          </Tabs>
        </>
      ) : isLoading ? (
        <Center mih={200}>
          <Loader />
        </Center>
      ) : isError ? (
        <Text color="red">
          Failed to load account: {(error as Error)?.message ?? 'Unknown error'}
        </Text>
      ) : (
        <Text color="dimmed">Account not found.</Text>
      )}
    </Stack>
  );
}
